/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, Dispatch, SetStateAction, Fragment, useMemo } from "react";
import Loader from 'elements/Loader'
import RowItem from "components/shareable/RowItem";
import Axios from "helpers/Interceptor";
import {
  Indicator as SpotifyIndicator,
  getSecondaryLabel,
  getPercentLabelColor,
} from "components/shareable/BrandConnectorIndicator";
import SpotifyIcon from "assets/images/streaming/square-icons/spotify-sqr.svg";
import {
  StreamsData,
  Platforms,
  ConnectorIndicatorTypes,
  PlaylistDatav2,
  CampaignMetadata,
  OverallCampaignStatus,
  CampaignDetails,
  StreamsChartData,
  FollowersData,
  IObjectKeys
} from "modules/Types";
import moment from "moment";
import { track } from "analytics";
import UnlockDataBanner from "components/shareable/UnlockDataBanner";
import UnlockPresavesByDayImage from "assets/images/unlock-presaves-by-day.svg";
import Intercom from "helpers/Intercom";
import { SymphonyTooltip } from "modules/Sty";
import QuestionMarkIcon from "assets/images/question-mark.svg";
import { Tab, Tabs } from "@material-ui/core";
import { clsx } from 'modules/Utils';
import dayjs from "dayjs";
import { CampaignDetailsContainer } from "../MusicStreams/styles";
import PlaylistingOverviewCampaignCard from "../Components/OverviewCampaignCard/playlisting";
import { playlistingRejectionIdToText } from "./utils";
import { DEFAULT_CURRENCY } from "modules/Const";
import getSymbolFromCurrency from "currency-symbol-map";
import UpgradeToProChip from "components/shareable/UpgradeToProChip";
import FacebookCampaignSimpleBanner from "../Components/FacebookCampaignIssuesBanner/FacebookCampaignSimpleBanner";
import useCurrentBrand from 'Hooks/useCurrentBrand';
import useScreen from "Hooks/useScreen";
import { getCampaign_api, getPlaylistCampaignStatus_api, getSpotifyStreams_api } from "api/campaignApi";
import { useHistory } from "react-router-dom";
import useCampaignDetails from "Hooks/useCampaignDetails";
import { CampaignExistsState } from "Hooks/useCampaignDetails";
import { AreaChart, Area, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";
import { CartesianGrid } from "recharts";
import { ResponsiveContainer } from "recharts";
import { getChartDivider, tooltipContentFormatter, tooltipLabelFormatter, yTickFormatter, xTickFormatter } from "components/shareable/ImpactChart/utils";
import { getBorderClassName } from "components/shareable/ImpactChart/utils";

/** Interface for playlisting campaign details */
export interface PlaylistingCampaignDetails extends CampaignDetails {
  /** Campaign metadata with playlisting-specific fields */
  campaign_metadata: PlaylistingCampaignMetadata;
  /** Reason for campaign denial if applicable */
  denied_reason?: string;
  /** Array of playlist data */
  playlistsData?: PlaylistDatav2[];
  /** Overall campaign status information */
  overallStatus?: {
    /** Current campaign status */
    status: OverallCampaignStatus;
    /** Optional status message/note */
    note?: string | null;
  };
}

export interface PlaylistingCampaignMetadata extends CampaignMetadata {
  // for playlisting campaigns 
  price_data: {
    tier: string;
    price: number;
  }
  currency: {
    code: string;
  }
}

export const initialStreamsData = {
  data: [] as StreamsChartData[],
  viewsGrowth: {
    currentGrowthValue: "",
    difference: 0,
    percentage: 0,
  },
  platform: "",
  stat: "",
} as StreamsData;


export const getIndicatorValue = (
  platform: Platforms.followers | Platforms.streams,
  data: FollowersData | StreamsData
) => {
  let dataByType = null;
  let value = 0;

  if (platform === Platforms.followers) {
    dataByType = data as FollowersData;
    value = dataByType?.growthData?.difference || 0;
  } else {
    dataByType = data as StreamsData;
    value = dataByType?.viewsGrowth?.difference || 0;
  }

  return value;
};

export const getIndicatorPercentage = (
  platform: Platforms.followers | Platforms.streams,
  data: FollowersData | StreamsData
) => {
  let percent = 0;
  let dataByType = null;

  if (platform === Platforms.followers) {
    dataByType = data as FollowersData;
    percent = dataByType?.growthData?.percentage || 0;
  } else {
    dataByType = data as StreamsData;
    const percentageFromData = dataByType?.viewsGrowth?.percentage || 0;
    percent = percentageFromData <= 0 ? 0 : percentageFromData;
  }

  return percent;
};

const getSpotifyStreamsIndicatorProps = (data: StreamsData) => {
  const value = getIndicatorValue(Platforms.streams, data);
  const percent = getIndicatorPercentage(Platforms.streams, data);
  const percentLabelColor = getPercentLabelColor(value);
  const showPercentIndicatorIcon = percent !== 0;
  const secondaryLabel = getSecondaryLabel(value, "total streams");

  return {
    icon: SpotifyIcon,
    alt: "spotify-icon.svg",
    mainLabel: "Spotify streams up",
    percentLabelColor,
    showPercentIndicatorIcon,
    secondaryLabel,
    percent,
    value,
    show: value > 0,
  };
};

const getImpactChartData = (
  spotifyStreamsData: StreamsData,
  campaignStartDate: string
) => {
  const chartData = spotifyStreamsData.data?.map(
    ({ datetime, views_stats }) => {
      const count = views_stats ? Number(views_stats) : 0;
      const splitted = datetime?.split("T")[0];
      return {
        type: ConnectorIndicatorTypes.SPOTIFY_STREAMS,
        day: moment(splitted).format("ddd"),
        count,
        date: datetime,
        campaignStartDate,
        valueOf: moment(splitted).valueOf(),
      };
    }
  );

  return chartData;
};

const getSpotifyStreams = async (args: {
  currentBrand: any;
  campaignDetails: any;
  contentSlug?: string;
  subtract?: boolean;
  setSpotifyStreamsData: Dispatch<SetStateAction<StreamsData>>;
}) => {
  try {
    const {
      currentBrand,
      campaignDetails,
      contentSlug,
      subtract,
      setSpotifyStreamsData,
    } = args;

    const createdAt = campaignDetails?.createdAt;
    const startDate = subtract
      ? moment(createdAt).subtract(3, 'days').format('YYYY-MM-DD')
      : moment(createdAt).format('YYYY-MM-DD');
    const endDate = moment(createdAt).add(45, 'days').format('YYYY-MM-DD');

    const spotifyStreamsData = await getSpotifyStreams_api({
      brandSlug: currentBrand.slug,
      contentSlug: contentSlug || '',
      startDate,
      endDate
    });

    if (spotifyStreamsData) {
      setSpotifyStreamsData(spotifyStreamsData);
    }
  } catch (error) {
    console.error('Error fetching Spotify streams:', error);
  }

};


interface Props {
  index?: number;
  isCompact?: boolean;
  width?: string;
  margin?: {
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
  };
  mainLabelComponent?: string | JSX.Element;
  chartData: {
    type: ConnectorIndicatorTypes;
    day: string;
    count: number;
    date: string;
    campaignStartDate: string;
    valueOf: number;
  }[];
  divider?: {
    top?: boolean;
    bottom?: boolean;
  };
  campaignStartDate?: string;
  customMainLabelComponents?: {
    changeAll?: boolean;
    components: {
      replacePosition: number;
      component: JSX.Element;
      props: IObjectKeys;
    }[];
  };
  weekly?: boolean
  showAverage?: boolean
  averageBeforeCampaign?: number;
  averageDuringCampaign?: number;
  isFirstDay?: boolean;
}

interface CustomizedDotProps {
  cx?: string | number;
  cy?: string | number;
  payload?: {
    campaignStartDate: string;
    count: number;
    date: string;
    day: string;
    type: ConnectorIndicatorTypes;
    valueOf: number;
  };
}

enum ReferenceLinePositions {
  start = "start",
  end = "end",
}

const { start, end } = ReferenceLinePositions;

const CustomizedDot = ({
  cx: centerX,
  cy: centerY,
  payload,
}: CustomizedDotProps) => {
  let [cx, cy] = [centerX || 0, centerY || 0];
  const splitted = payload?.campaignStartDate.split("T")[0];
  const campaignStartDateValueOf = moment(splitted).valueOf();
  const showDot = campaignStartDateValueOf === payload?.valueOf;

  if (typeof cx === "string") cx = Number(centerX);
  if (typeof cy === "string") cy = Number(centerY);

  return (
    <Fragment>
      {showDot && (
        <svg
          x={cx - 6.5}
          y={cy - 6.5}
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="6.5" cy="6.5" r="6.5" fill="#07C806" />
        </svg>
      )}
    </Fragment>
  );
};



const getReferenceLineLabelPosition = (
  x: number,
  y: number,
  index: number,
  totalLabels: number,
  position?: ReferenceLinePositions,
  isHorizontal?: boolean,
  yOffset: number = 0
) => {
  if (isHorizontal) {
    if (yOffset > 0) {
      return { x: x + 10, y: y + (yOffset) };
    }
    return { x: x + 10, y: y - 35 };
  }
  if (position === start) return { x: x - 100, y };
  if (position === end) return { x: x + 10, y };
  return { x, y };
};

const CustomReferenceLineLabel = ({
  viewBox,
  value,
  position,
  isHorizontal,
  label,
  index,
  totalLabels,
  yOffset = 0
}: CustomReferenceLineLabelProps) => {
  const { x, y } = viewBox;
  const { x: fixedX, y: fixedY } = getReferenceLineLabelPosition(x, y, index, totalLabels, position, isHorizontal, yOffset);

  const formatValue = (val: string | number | null | undefined) => {
    if (val === null) return null;
    if (typeof val === 'number' || (typeof val === 'string' && !isNaN(Number(val)))) {
      return Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 });
    }
    return val;
  };

  const formattedValue = formatValue(value);

  return (
    <text
      x={fixedX}
      y={fixedY}
      fontSize={12}
      className="font-semibold"
      textAnchor="start"
      dominantBaseline="text-before-edge"
    >
      {isHorizontal ? (
        <>
          <tspan x={fixedX} dy="0em">{label}{formattedValue !== null ? ':' : ''}</tspan>
          {formattedValue !== null && <tspan x={fixedX} dy="1.2em">{formattedValue}</tspan>}
        </>
      ) : (
        <>
          <tspan x={fixedX} dy=".6em">
            Campaign Start
          </tspan>
          {formattedValue !== null && (
            <tspan x={fixedX} dy="1.2em">
              {formattedValue}
            </tspan>
          )}
        </>
      )}
    </text>
  );
};



interface CustomReferenceLineLabelProps {
  position?: ReferenceLinePositions;
  viewBox: {
    height: number;
    width: number;
    x: number;
    y: number;
  };
  value?: string | number | null;
  isHorizontal?: boolean;
  label?: string;
  index: number;
  totalLabels: number;
  yOffset?: number;
}


export enum findMinMaxCases {
  minimum = "minimum",
  maximum = "maximum",
}

export const findClosest = (value: number, arr: number[]) => {
  const differences = arr.map((el) => Math.abs(el - value));
  const min = Math.min.apply(Math, differences);
  const index = differences.indexOf(min);
  const minimum = arr[index];
  const areEqual = minimum === 0;
  return { minimum, index, areEqual };
};

export const findMinMax = (arr: number[], find: findMinMaxCases) => {
  if (findMinMaxCases.maximum === find) return Math.max.apply(Math, arr);
  if (findMinMaxCases.minimum === find) return Math.min.apply(Math, arr);
  return undefined;
};

const ImpactChart = ({
  index,
  chartData,
  divider,
  campaignStartDate,
  mainLabelComponent,
  width,
  margin,
  weekly,
  showAverage,
  averageBeforeCampaign,
  averageDuringCampaign,
  isFirstDay,
  isCompact
}: Props) => {
  const splitted = campaignStartDate?.split("T")[0];
  const campaignValueOf = moment(splitted).valueOf();
  const datesValuesOf = chartData.map((data) => data.valueOf);
  const datesCounts = chartData.map((data) => data.count);
  const { index: closestIndex } = findClosest(campaignValueOf, datesValuesOf);
  const maximumCountValue =
    findMinMax(datesCounts, findMinMaxCases.maximum) || 0;
  const minimumCountValue =
    findMinMax(datesCounts, findMinMaxCases.minimum) || 0;
  const { top: dividerTop, bottom: dividerBottom } = getChartDivider(divider);
  const borderClassName = getBorderClassName(dividerTop, dividerBottom);
  const referenceLineX = chartData[closestIndex]?.valueOf;
  const referenceLineLabelValue = moment(splitted).format("M/DD");
  const computedMinimumYValue = Math.floor(0.97 * minimumCountValue);
  const setAsAuto = maximumCountValue === 0 && minimumCountValue === 0;
  const referenceLineXLabelPosition =
    closestIndex === chartData.length - 1 ? start : end;

  const totalLabels = useMemo(() => {
    let count = 1; // Start with 1 for the campaign start label
    if (showAverage && averageBeforeCampaign !== undefined && chartData.filter(data => data.valueOf < referenceLineX).length > 3) {
      count++;
    }
    if (!isFirstDay && showAverage && averageDuringCampaign !== undefined) {
      count++;
    }
    return count;
  }, [showAverage, averageBeforeCampaign, averageDuringCampaign, isFirstDay, chartData, referenceLineX]);

  const checkOverlap = (value1: number, value2: number) => {
    return Math.abs(value1 - value2) < 0.05 * maximumCountValue;
  };

  const getYOffset = (index: number) => {
    const baseOffset = 5;
    let offset = index * baseOffset;

    // Check for overlap with Campaign Start label
    if (index === 2 && checkOverlap(averageDuringCampaign || 0, Number(referenceLineLabelValue))) {
      while (checkOverlap(averageDuringCampaign || 0, Number(referenceLineLabelValue))) {
        offset += 5; // Move down by 5 pixels at a time
      }
    }

    return offset;
  };

  let minTickGap = chartData.length > 4 ? 30 : 0
  return (
    <div className={`${borderClassName} pb-4`} key={index}>
      {mainLabelComponent ? mainLabelComponent : null}
      <ResponsiveContainer width={width ? width : window.innerWidth < 768 ? "100%" : "95%"} height={200}>
        <AreaChart data={chartData} margin={margin || { top: 8 }}>
          <CartesianGrid vertical={false} horizontal strokeDasharray="3 3" />
          <XAxis
            dataKey="valueOf"
            domain={["auto", "auto"]}
            axisLine={false}
            tickLine={false}
            tickFormatter={xTickFormatter(chartData, weekly)}
            minTickGap={minTickGap}
          />
          <YAxis
            tickCount={5}
            dataKey="count"
            domain={[setAsAuto ? "auto" : computedMinimumYValue, "auto"]}
            axisLine={false}
            tickLine={false}
            tickFormatter={yTickFormatter}
          />
          <Tooltip
            labelFormatter={tooltipLabelFormatter}
            formatter={tooltipContentFormatter}
          />

          <Area
            type="monotone"
            dataKey="count"
            stroke="#42c221"
            fill={"#d9ffd9"}
            fillOpacity="0.8"
            activeDot={{ r: 8 }}
            strokeWidth={4}
            dot={<CustomizedDot />}
            isAnimationActive={false}
          />
          <ReferenceLine
            strokeWidth={2}
            strokeDasharray="3 4"
            x={referenceLineX}
            stroke="#42c221"
            label={(props) => (
              <CustomReferenceLineLabel
                {...props}
                value={referenceLineLabelValue}
                position={referenceLineXLabelPosition}
                index={0}
                totalLabels={totalLabels}
                yOffset={getYOffset(0)}
              />
            )}
          />
          {showAverage && averageBeforeCampaign !== undefined && chartData.filter(data => data.valueOf < referenceLineX).length > 3 && (
            <ReferenceLine
              stroke="red"
              strokeDasharray="3 3"
              segment={[
                { x: chartData[0].valueOf, y: averageBeforeCampaign },
                { x: referenceLineX, y: averageBeforeCampaign }
              ]}
              label={(props) => (
                <CustomReferenceLineLabel
                  {...props}
                  value={averageBeforeCampaign.toFixed(2)}
                  isHorizontal={true}
                  label={isCompact ? "Average before" : "Average before Campaign"}
                  index={1}
                  totalLabels={totalLabels}
                />
              )}
            />
          )}
          {!isFirstDay && showAverage && averageDuringCampaign !== undefined && (
            <ReferenceLine
              stroke="purple"
              strokeWidth={4}
              strokeDasharray="3 3"
              segment={[
                { x: referenceLineX, y: averageDuringCampaign },
                { x: chartData[chartData.length - 1].valueOf, y: averageDuringCampaign }
              ]}
              label={(props) => (
                <CustomReferenceLineLabel
                  {...props}
                  value={null}
                  isHorizontal={true}
                  position={end}
                  label="New Average"
                  index={2}
                  totalLabels={totalLabels}
                  yOffset={getYOffset(2)}
                />
              )}
            >
              <animate attributeName="stroke-opacity" values="0;1;0" dur="3s" repeatCount="indefinite" />
            </ReferenceLine>
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const PlaylistingCampaignDetails = () => {
  const history = useHistory()
  const {
    campaign: campaignDetailsState,
  } = useCampaignDetails() as CampaignExistsState

  const campaignDetails = campaignDetailsState as PlaylistingCampaignDetails

  const { mobileView } = useScreen();
  const { isProBrand, currentBrand } = useCurrentBrand();

  const { campaign_metadata, id, createdAt, denied_reason } = campaignDetails
  const { content, price_data, currency } = campaign_metadata
  const currencyCode = currency?.code || DEFAULT_CURRENCY

  const {
    name: contentName,
    thumbnail_url: contentThumbnail,
    slug: contentSlug,
  } = content

  const [isLoading, setLoading] = useState(true)
  const [playlistsData, setPlaylistsData] = useState<PlaylistDatav2[] | null>(null)
  const [spotifyStreamsChartData, setSpotifyStreamsChartData] = useState<StreamsData>(initialStreamsData)
  const [spotifyStreamsIndicatorsData, setSpotifyStreamsIndicatorsData] = useState<StreamsData>(initialStreamsData)
  const [campaignDetailsLocal, setCampaignDetailsLocal] = useState<any>({
    createdAt: "",
    status: "IN_REVIEW",
    campaign_metadata: {
      playlists: [],
      campaign_version: 'v1',
      note: null,
      price_data: {}
    }
  })
  const [overallCampaignStatus, setOverallCampaignStatus] = useState<any>({
    status: "pending",
    note: null,
  })

  const campaignStartDate = moment(campaignDetailsLocal?.createdAt).format("YYYY-MM-DD")

  // tabs
  const [playlistsView, setPlaylistsView] = useState<string>('active')

  async function getCampaignDetails() {
    const campaign = await getCampaign_api(id);
    setCampaignDetailsLocal(campaign);
  }
  useEffect(() => {
    if (campaignDetails) {
      track("Campaign Details Viewed", {
        type: "get_playlisted_id",
        brand_id: currentBrand?.id,
        brand_name: currentBrand?.name,
        brand_slug: currentBrand?.slug,
        content_name: content.name,
        content_slug: content.slug,
        content_id: content.id,
        tier: price_data.tier,
        price: price_data.price,
      });
    }

    if (campaignDetails && currentBrand) {
      getCampaignDetails();
    } else {
      console.log("could not get all data");
    }
  }, []);

  useEffect(() => {
    if (campaignDetailsLocal.createdAt) {
      const mainArgs = {
        currentBrand,
        contentSlug,
        campaignDetails: campaignDetailsLocal,
      };

      const {
        campaign_metadata,
        status,
      } = campaignDetailsLocal

      const {
        playlists,
        campaign_version = 'v1'
      } = campaign_metadata

      if ((!playlists && status === 'ACTIVE')
        || campaign_version !== 'v2') {
        getPlaylistStatuses()
      } else {
        setPlaylistsData(playlists)
        const totalFollowers = getTotalActiveFollowers(playlists)
        switch (status) {
          case 'DENIED':
            const msg = campaign_metadata && campaign_metadata.note ? playlistingRejectionIdToText(campaign_metadata.note) : null
            setOverallCampaignStatus({
              status: 'denied',
              note: msg
            })
            break;
          case 'IN_REVIEW':
            setOverallCampaignStatus({
              status: "in_review"
            })
            break;

          // TODO: this is where we check for playlists
          case 'ACTIVE':
            if (totalFollowers > price_data.tier) {
              setOverallCampaignStatus({
                status: "active",
                note: `Your song has been successfully accepted by playlists with a total of ${totalFollowers.toLocaleString('en')} followers!`,
              });
            } else {
              setOverallCampaignStatus({
                status: "active",
                note: "Your song has been accepted by our playlisting team! As playlists are being pitched, you'll be able to see updates here.",
              });
            }

            break;
          case 'FINISHED':
            setOverallCampaignStatus({
              status: "finished",
              note: `Your song has been successfully accepted by playlists with a total of ${totalFollowers.toLocaleString('en')} followers! You can review how long your song will be playlisted in the list below.`,
            });
            break;
          case 'PROCESSING':
            setOverallCampaignStatus({
              status: "processing",
              note: "Your payment is being processed. Once it's complete, we'll start pitching your song to playlists.",
            });
            break;
        }

      }

      getSpotifyStreams({
        ...mainArgs,
        subtract: true,
        setSpotifyStreamsData: setSpotifyStreamsChartData,
      });
      getSpotifyStreams({
        ...mainArgs,
        subtract: false,
        setSpotifyStreamsData: setSpotifyStreamsIndicatorsData,
      });
      setLoading(false);
      console.log("got all data");
    } else {
      //TODO: Add error handler
      console.log("could not get all data");
    }
  }, [campaignDetailsLocal.createdAt]);

  const getPlaylistStatuses = async () => {
    try {
      const playlistsData = await getPlaylistCampaignStatus_api(
        currentBrand?.id || '',
        campaignDetails.id
      );
      if (!playlistsData?.length) {
        setOverallCampaignStatus({
          status: 'in_review'
        });
        return;
      }

      setPlaylistsData(playlistsData);

      // sum up total followers
      const totalFollowers = playlistsData.reduce(
        (prevValue: any, currentValue: any) => {
          return prevValue + currentValue.playlistFollowers;
        },
        0
      );

      const tier = price_data.tier;
      if (totalFollowers < tier) {
        setOverallCampaignStatus({
          status: "active",
          note: "Your song has started being placed to our network of vetted playlists. As your song is added to more playlists, you'll be able to see updates here.",
        });
      } else if (totalFollowers >= tier) {
        setOverallCampaignStatus({
          status: 'active',
          note: null,
        });
      }
    } catch (e: any) {
      setOverallCampaignStatus({
        status: campaignDetailsLocal.status.toLowerCase(),
        note: 'Campaign status may not be up-to-date. Please refresh the page or check back later.',
      });
    }
  }

  const adsStatusText = () => {
    switch (overallCampaignStatus.status) {
      case "pending":
        return undefined;
      case "active":
        return "active"
      case "error":
        return "issues"

      case "in_review":
        return "in_review"
      case "live_with_issues":
        return "live"
      case "denied":
        return "declined"
      case "finished":
        return "pitched"
      case "processing":
        return "processing"
      default:
        return undefined
    }
  }

  const showAdsStatusMessage = () => {
    const framework = (props: {
      title?: string,
      description: string | JSX.Element,
      type?: "success" | "warning"
    }) => {
      const {
        title,
        description,
        type
      } = props

      return (<div className="py-5 px-5 pb-0 lg:px-10">

        <div className="rounded-md"
          style={{
            backgroundColor: type === 'success' ? "#ebfaeb" : "#FDF6EC"
          }}>
          <div className="p-5">
            {title && (<p style={{
              color: type === 'success' ? "#07C806" : "#754600"
            }}>{title}</p>)}
            <p style={{
              color: "black"
            }}>
              {description}
              {/* <button
                className="font-medium"

                style={{
                  color: "#8800FF"
                }}
                onClick={() => {
                  Intercom.openInstagramBusinessAccountArticle()
                }}>Learn how to switch to a Business Account &gt;</button> */}
            </p>
          </div>

        </div>
      </div>)
    }
    switch (overallCampaignStatus.status) {
      case "pending":
        return null;

      case "active":
        if (!overallCampaignStatus.note) {
          return null;
        } else {
          return framework({
            description: overallCampaignStatus.note,
            type: "success"
          })
        }
      case 'finished':
        return framework({
          description: overallCampaignStatus.note,
          type: "success"
        })

      case "error":
        return (
          <div className="py-4 rounded-sm bg-red-500 text-white py-1 text-md px-3 text-center">
            <p className="w-8/12 m-auto">{overallCampaignStatus.note}</p>
          </div>
        );
      case "in_review":
        return framework({
          title: "Your playlisting campagn is being reviewed.",
          description: "Our curator team is reviewing your campaign for eligibility. We'll let you know if we have any questions and start pitching automatically once your campaign is approved."
        })

      case "live_with_issues":
        return (
          <div className="py-4 rounded-sm bg-green-500 text-white py-1 text-md px-3 text-center">
            <p className="w-8/12 m-auto">{overallCampaignStatus.note}</p>
          </div>
        );
      case "denied":
        if (denied_reason === 'unsuccessful_payment') return null
        return framework({
          title: "We weren't able to playlist your song.",
          description: (
            <>
              <span>Unfortunately we weren't able to find any playlists this time around that would drive meaningful engagement.
                You will be sent a full refund of {getSymbolFromCurrency(currencyCode)}{campaign_metadata.price_data.price.toLocaleString('en', {
                  minimumFractionDigits: 2
                })}.
              </span>
              {overallCampaignStatus.note && (
                <>
                  <br />
                  <br />
                  <span>Here are campaign rejection notes from your curators:</span>
                  <br />
                  <span>"{overallCampaignStatus.note}"</span>
                  <br />
                  <br />
                  <span>We recommend submitting different songs to see which one may be a best fit. Over 95% of playlisting campaigns are accepted and exceed their goal.</span>
                  <br />
                  <button
                    className="mt-2 font-medium"

                    style={{
                      color: "#8800FF"
                    }}
                    onClick={() => {
                      Intercom.openPlaylistingArticle()
                    }}>Learn how our curated pitching process works &gt;</button>
                </>

              )}
            </>
          ),
          type: "warning"
        })
    }
  }

  const returnColorStylingByStatus = (status: string) => {
    if (status === 'Pitching') return 'bg-green-200 text-black'
    else if (status === 'Declined') return 'bg-yellow-400'
    else if (status === 'Accepted') return 'bg-green-400 text-white'
  }

  const returnPlaylistsView = () => {
    let passedMsg = null
    if (playlistsData?.filter((o: PlaylistDatav2) => {
      return o.playlistStatus === 'Declined'
    }).length === 0) {
      // there are 0 declined items
      passedMsg = `Our team reaches out to a variety of high-engagement playlists as part of your pitch. Any playlists that don't accept your song can be tracked here.`

    } else {
      passedMsg = `Our team pitched these playlists, but we weren't able to secure a playlisting spot. We'll keep pitching until we secure some good playlists.`
    }

    return (
      <div className="px-2 md:px-6 h-120">
        <ul className="max-h-80 overflow-auto">
          {playlistsView === 'passed' ? (
            <li>
              <div className="sy-card">

                <div className="rounded-md"
                  style={{
                    backgroundColor: "#FDF6EC"
                  }}>
                  <div className="p-5">
                    <p style={{
                      color: "#754600"
                    }}>{passedMsg}</p>

                  </div>

                </div>
              </div>
            </li>
          ) : (null)}
          {playlistsData
            ?.sort((a: PlaylistDatav2, b: PlaylistDatav2) => {
              return b.playlistFollowers - a.playlistFollowers;
            })
            .sort((a: PlaylistDatav2, b: PlaylistDatav2) => {
              if (a.playlistStatus === "Accepted" && b.playlistStatus !== "Accepted") {
                return -1;
              } else if (a.playlistStatus !== "Accepted" && b.playlistStatus === "Accepted") {
                return 1;
              }

              if (a.playlistStatus === "Pitching" && b.playlistStatus !== "Pitching") {
                return -1;
              } else if (a.playlistStatus !== "Pitching" && b.playlistStatus === "Pitching") {
                return 1;
              }

              else {
                return 0;
              }
            })
            .filter((a: PlaylistDatav2) => {
              if (playlistsView === 'active') {
                return (a.playlistStatus === 'Pitching' || a.playlistStatus === 'Accepted') || !a.playlistStatus
              } else if (playlistsView === 'passed') {
                return a.playlistStatus === 'Declined';
              }

            })
            .map((item: PlaylistDatav2) => {

              const totalRemainingDays = item.playlistStatus === 'Accepted'
                && item.addedAt
                && item.estimatedRemovalDate ? dayjs(item.addedAt).add(30, 'days').diff(dayjs(), 'days') : 0

              return (
                <RowItem
                  key={item.playlistUrl}
                  // secondRowContent={

                  // }
                  imageStyles={`h-6`}
                  name={(
                    <div className="flex flex-row justify-center items-center">
                      {item.playlistImages && (<div className="w-12 h-12">
                        <img className="flex-shrink-0 w-12 h-12 rounded-md" src={item.playlistImages!.url} alt={item.playlistName} />
                      </div>)}
                      <div className="ml-2">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-center">
                          <div>{item.playlistName}</div>
                          {item.playlistStatus && (
                            <div className={`${clsx(returnColorStylingByStatus(item.playlistStatus), "text-xs py-1 px-2 rounded-md md:ml-2 uppercase")}`}>
                              <div>{item.playlistStatus}</div>
                            </div>
                          )}

                        </div>
                        <div className="mt-1 flex flex-row items-center">
                          <a
                            target="_blank"
                            href={item.playlistUrl}
                            className="text-green-500 text-xs"
                            rel="noreferrer"
                          >
                            View on Spotify
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                  showRightValue={true}
                  rightValue={(
                    <div className="flex items-end justify-content flex-col text-right">
                      <p>{item.playlistFollowers.toLocaleString(
                        "en"
                      )} Followers</p>
                      {item.playlistStatus === 'Accepted' &&
                        item.playlistPosition && (<p className="text-sm">Position: {item.playlistPosition}{item.totalTracks && `/${item.totalTracks}`}</p>)}
                      {item.playlistStatus === 'Accepted' &&
                        item.addedAt && (<p className="text-sm">Added On: {dayjs(item.addedAt).format("MMM D, YYYY")}</p>)}
                      {item.playlistStatus === 'Accepted' && item.estimatedRemovalDate && item.addedAt ?
                        totalRemainingDays &&
                          totalRemainingDays > 0 ? (<p className="text-sm">~{totalRemainingDays} Day{totalRemainingDays === 1 ? '' : 's'} Left on Playlist</p>) : (<p className="text-sm text-green-600">Playlisting Period Completed</p>) : null}
                    </div>
                  )}
                />
              );
            })}
        </ul>
      </div>
    )
  }

  const getTotalActiveFollowers = (playlists?: PlaylistDatav2[]) => {
    var playlistsToView = playlistsData ? playlistsData : playlists
    const acceptedPlaylists = playlistsToView?.filter((playlist: PlaylistDatav2) => (!playlist.playlistStatus || playlist.playlistStatus === 'Accepted'))
    const totalFollowers = acceptedPlaylists ? acceptedPlaylists!
      .reduce(
        (prevValue: any, currentValue: any) => {
          return prevValue + currentValue.playlistFollowers;
        },
        0
      ) : 0;

    return totalFollowers
  }

  const showPlaylistingData = () => {
    let acceptedPlaylists = playlistsData?.filter((playlist: PlaylistDatav2) => (!playlist.playlistStatus || playlist.playlistStatus === 'Accepted'))
    if (!acceptedPlaylists) acceptedPlaylists = []

    const totalFollowers = getTotalActiveFollowers(acceptedPlaylists)
    const spotifyStreamsIndicatorProps = getSpotifyStreamsIndicatorProps(
      spotifyStreamsIndicatorsData
    );

    const { value } = spotifyStreamsIndicatorProps;

    const impactChartData = getImpactChartData(
      spotifyStreamsChartData,
      campaignStartDate
    );

    if (overallCampaignStatus.status === 'denied') return null

    return (
      <>
        <div className="px-6 lg:px-10 py-5 mt-2">
          <p className="text-lg font-semibold">Performance</p>
        </div>
        <div className="px-6 lg:px-10 py-5 border-b border-t flex flex-row justify-between">
          <div className="w-5/12">
            <p className="text-md font-normal ">Total Followers Reached</p>
            <p className="text-xl font-semibold mt-2">
              {totalFollowers?.toLocaleString("en")}
            </p>
          </div>
          <div className="w-3/12">
            <p className="text-md font-normal ">Total Playlists</p>
            <p className="text-xl font-semibold mt-2">
              {acceptedPlaylists?.length.toLocaleString("en")}
            </p>
          </div>
          <div className="w-5/12 grid justify-items-end">
            {
              isProBrand && spotifyStreamsIndicatorProps.show &&
              <SpotifyIndicator {...spotifyStreamsIndicatorProps} />
            }
          </div>
        </div>
        <div className="px-6 lg:px-10 py-5 mt-2">
          <p className="text-lg font-semibold  flex">Playlists <button className="ml-1.5 w-5 h-5"
            onClick={() => {
              Intercom.openPlaylistingArticle()
            }}>
            <SymphonyTooltip className="block w-6 h-6 cursor-pointer" title={
              "Click to learn more about how the playlisting process works."
            } arrow>
              <img
                className="w-5 h-5"
                alt="question-mark-icon.svg"
                src={QuestionMarkIcon}
              />
            </SymphonyTooltip>
          </button></p>
          <p className="text-sm text-gray-500">
            These are the playlists we're pitching for your campaign.
          </p>
        </div>
        {playlistsData && playlistsData.length > 0 ? (
          <>
            <Tabs
              scrollButtons="auto"
              variant="scrollable"
              value={playlistsView}
              onChange={(event, newValue) => setPlaylistsView(newValue)}
              aria-label="Settings Options Selector"
              TabIndicatorProps={{
                style: {
                  backgroundColor: "#8800FF"
                }
              }}>

              <Tab
                disableRipple
                value={'active'}
                label={<span
                  className={"capitalize font-medium text-base px-4 py-2"}>Active</span>} />
              <Tab
                disableRipple
                value={'passed'}

                label={<span
                  className={"capitalize font-medium text-base px-4 py-2"}>Passed</span>} />

            </Tabs>
            {returnPlaylistsView()}

          </>) : (
          <div className="sy-card pt-0">
            <div className="rounded-md"
              style={{
                backgroundColor: "#FDF6EC"
              }}>
              <div className="p-5">
                <p style={{
                  color: "#754600"
                }}>Once our playlisting team starts pitching to playlists, you'll be able to track the status of each playlist pitch here.</p>

              </div>

            </div>
          </div>
        )}

        {Boolean(impactChartData.length) && (
          <div className="px-6 lg:px-10 py-5 border-b border-t flex flex-col justify-between">
            <div className="w-full">
              <p className="text-lg font-semibold flex flex-row items-center gap-1">Impact<UpgradeToProChip type="gem" color="purple" height={18} width={18} /></p>
            </div>
            {isProBrand ? (
              <div className="w-full mt-4">
                <ImpactChart
                  {...{ campaignStartDate }}
                  width="100%"
                  weekly={
                    (impactChartData.length >= 14 && mobileView) ||
                    impactChartData.length >= 28
                  }
                  margin={{ top: 8, left: -10, right: 15 }}
                  chartData={impactChartData}

                />
              </div>
            ) : (
              <>
                <div className="w-full mt-4">
                  <UnlockDataBanner
                    source="Playlisting Campaign Details - Impact Stats"
                    title="Track exactly how your marketing is growing your fanbase"
                    description="Go Pro to see how playlisting is growing your Spotify streams"
                    image={UnlockPresavesByDayImage}
                    hideImage={mobileView}
                  />
                </div>
                <div className="w-full mt-4">
                  <img src={require("../../../assets/images/campaignDetails/impactBlurredSpotify.png").default} />
                  <img src={require("../../../assets/images/campaignDetails/impactBlurredChart.png").default} />
                </div>
              </>
            )}
          </div>
        )}
      </>
    );
  }

  const getErrorDescriptionBanner = () => {
    if (overallCampaignStatus.status === 'processing') {
      return (
        <FacebookCampaignSimpleBanner
          type="issues"
          title="Your payment is still being processed."
          description="Once your payment is successfully processed, your campaign will be submitted directly. You'll only be charged if your payment is processed fully, otherwise you won't be charged for your campaign."
        />
      )
    }

    if (overallCampaignStatus.status === 'denied' && denied_reason === 'unsuccessful_payment') {
      return (
        <FacebookCampaignSimpleBanner
          type="issues"
          title="Your payment was unable to be processed."
          description="Unfortunately your payment provider wasn't able to successfully process your campaign payment. You won't be charged and you can create a new campaign if you want to re-submit."
        />
      )
    }
    return null
  }

  return (
    <div
      className="playlistingCampaignDetailsComponent"
    >
      <PlaylistingOverviewCampaignCard
        isLoading={isLoading}
        goBack={() => history.goBack()}
        name={contentName}
        thumbnail={contentThumbnail}
        campaignStatus={campaignDetailsLocal && campaignDetailsLocal.status ? campaignDetailsLocal?.status.toLowerCase() : undefined}
        campaignStatusLabel={overallCampaignStatus && overallCampaignStatus.status ? adsStatusText() : undefined}
      />

      {getErrorDescriptionBanner()}

      <div className=" bg-white shadow-none lg:shadow-lg pb-6 middle-wrapper
      justify-start">

        <div className="px-6 lg:px-10 py-5 border-b flex flex-row justify-between">
          <p className="text-lg font-semibold">Goal</p>
          <p className="text-lg text-right">
            {price_data.tier.toLocaleString()} Followers across Playlists
          </p>
        </div>
        <div className="px-6 lg:px-10 py-5 border-b flex flex-row justify-between">
          <p className="text-lg font-semibold">Campaign Submitted</p>
          <p className="text-lg text-right">
            {dayjs(createdAt).format("MMM DD, YYYY")}
          </p>
        </div>
        {showAdsStatusMessage()}

        {/* <div className="px-6 lg:px-10 py-5 border-b flex flex-row justify-between">
        <p className="text-lg font-semibold">Budget</p>
        <p className="text-lg">${millify(price_data.price)}</p>
      </div> */}
        {
          isLoading ? (
            <div className="mx-auto my-6 flex-col flex items-center ">
              <Loader />
              <p className="mt-1 text-center">Loading Campaign Data...</p>
            </div>
          ) : (
            showPlaylistingData()
          )
        }
      </div>
    </div>
  );
}

export default PlaylistingCampaignDetails;